import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Facebook, Youtube } from "lucide-react";
import SpikeLogo from "@/components/site/SpikeLogo";
import { useSettings } from "@/lib/settings";

const Footer = () => {
  const { show_membership, show_shop } = useSettings();
  return (
    <footer
      data-testid="site-footer"
      className="bg-spike-elevated border-t border-white/10 stripe-pattern"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="md:col-span-5">
            <SpikeLogo />
            <p className="mt-6 text-spike-text-muted font-exo text-sm leading-relaxed max-w-md">
              Home of Canadian Future Stars. A network of indoor training clubs
              built for serious cricket and baseball players — six lanes,
              pro-grade machines, retail under one roof.
            </p>
            <div className="mt-8 flex items-center gap-4">
              {[
                { icon: Instagram, href: "#", id: "instagram" },
                { icon: Facebook, href: "#", id: "facebook" },
                { icon: Youtube, href: "#", id: "youtube" },
              ].map((s) => (
                <a
                  key={s.id}
                  href={s.href}
                  data-testid={`footer-social-${s.id}`}
                  className="w-10 h-10 border border-white/10 flex items-center justify-center text-spike-text-muted hover:text-spike-text hover:border-spike-blue hover:bg-spike-blue/10 transition-all"
                  aria-label={s.id}
                >
                  <s.icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          {/* Explore */}
          <div className="md:col-span-3">
            <h4 className="font-orbitron text-xs font-bold uppercase tracking-[0.25em] text-spike-blue-light mb-5">
              Explore
            </h4>
            <ul className="space-y-3 font-exo text-sm">
              {[
                { to: "/", label: "Home" },
                { to: "/book", label: "Book Nets" },
                ...(show_membership ? [{ to: "/membership", label: "Membership" }] : []),
                ...(show_shop ? [{ to: "/", label: "Pro Shop" }] : []),
              ].map((l, i) => (
                <li key={i}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-spike-text-muted hover:text-spike-text transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="font-orbitron text-xs font-bold uppercase tracking-[0.25em] text-spike-blue-light mb-5">
              Flagship — Mississauga
            </h4>
            <ul className="space-y-4 font-exo text-sm text-spike-text-muted">
              <li className="flex gap-3">
                <MapPin className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>7289 Torbram Rd., Mississauga, ON L4T 1G8</span>
              </li>
              <li>
                <a
                  href="tel:+19056789725"
                  data-testid="footer-phone-link"
                  className="flex gap-3 hover:text-spike-text transition-colors"
                >
                  <Phone className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" strokeWidth={1.5} />
                  (905) 678-9725
                </a>
              </li>
              <li>
                <a
                  href="mailto:spikesportscanada@gmail.com"
                  data-testid="footer-email-link"
                  className="flex gap-3 hover:text-spike-text transition-colors break-all"
                >
                  <Mail className="w-4 h-4 text-spike-blue-light mt-0.5 shrink-0" strokeWidth={1.5} />
                  spikesportscanada@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-exo text-xs text-spike-dim tracking-wider uppercase">
            © {new Date().getFullYear()} Spike Sports. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
            <Link to="/terms" data-testid="footer-terms-link" className="font-orbitron uppercase tracking-[0.2em] text-spike-text-muted hover:text-spike-blue-light">
              Terms
            </Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="font-orbitron uppercase tracking-[0.2em] text-spike-text-muted hover:text-spike-blue-light">
              Privacy
            </Link>
            <Link to="/waiver-qr" data-testid="footer-waiver-qr-link" className="font-orbitron uppercase tracking-[0.2em] text-spike-text-muted hover:text-spike-blue-light">
              Sign Waiver
            </Link>
            <p className="font-orbitron text-[0.65rem] tracking-[0.4em] uppercase text-spike-blue-light/70">
              Train. Hit. Repeat.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
